import React, { useEffect, useState, useRef, useCallback } from 'react';

import { SideMenu } from './common/side-menu/SideMenu';
import { Construction } from './construction/Construction';
import { Toaster } from './common/toaster/Toaster';
import { ClientsSearch } from './clients/clients-search/ClientsSearch';
import { ClientsCreate } from './clients/clients-create/ClientsCreate';
import { ClientsView } from './clients/clients-view/ClientsView';
import { StudyTrialSearch } from './studies-and-trials/study-and-trial-search/StudyTrialSearch';
import { StudyTrialCreate } from './studies-and-trials/study-and-trial-create/StudyTrialCreate';
import { UsersSearch } from './admin/users/users-search/UsersSearch';
import { ParticipantReviewSearch } from './reviews-and-queries/participant/search/ParticipantReviewSearch';
import { CAPAReviewSearch } from './reviews-and-queries/capa/search/CAPAReviewSearch';
import { FormAdministrationSearch } from './studies-and-trials/form-administration/search/FormAdministrationSearch';
import { ParticipantsSearch } from './studies-and-trials/participants/search/ParticipantsSearch';
import { ParticipantsLinkNew } from './studies-and-trials/participants/link-new/ParticipantsLinkNew';
import { CreateNewForm } from './studies-and-trials/form-administration/create/CreateNewForm';
import { ParticipantReviewCreate } from './reviews-and-queries/participant/create/ParticipantReviewCreate';
import { CAPAReviewCreate } from './reviews-and-queries/capa/create/CAPAReviewCreate';
import { FacilitiesSearch } from './admin/facilities/FacilitiesSearch';
import { CAPAReviewEdit } from './reviews-and-queries/capa/edit/CAPAReviewEdit';
import { ParticipantReviewEdit } from './reviews-and-queries/participant/edit/ParticipantReviewEdit';
import { Dashboard } from './dashboard/Dashboard';
import { StudyTrialEdit } from './studies-and-trials/study-and-trial-edit/StudyTrialEdit';
import { ClientSecurityQuestions } from './admin/security-questions/ClientSecurityQuestions';
import { GenericQueryComments } from './admin/query-comments/GenericQueryComments';
import { CapaNotificationSettings } from './admin/capa-notification/CapaNotificationSettings';
import { UsersView } from './admin/users/users-view/UsersView';
import { ParticipantsEditTab } from './studies-and-trials/participants/edit/ParticipantsEditTab';
import { DataManagement } from './reports/data-management/DataManagement';
import { QualityControl } from './reports/quality-control/QualityControl';
import { Settings } from './settings/Settings';
import { NoAccess } from './no-access/NoAccess';

import { ReactComponent as AurumLogo } from '../resources/images/aurum-logo-landscape-200.svg';
import { ReactComponent as ClineaseLogo } from '../resources/images/clinease-logo-landscape-200.svg';

import './View.css';

export const View = ({ keycloak, context, setContext, setSpinnerText, toolTipsEnabled }) => {
  const [showMenu, setShowMenu] = useState(true);
  const [activeScreen, setActiveScreen] = useState(context.currentScreen);
  const [toasterData, setToasterData] = useState(null);

  const screenMap = useRef(new Map()).current;

  const navigateTo = (screenKey) => {
    setContext((prevState) => ({
      ...prevState,
      currentScreen: screenKey,
      viewStack: [...prevState.viewStack, { screen: screenKey, externalReference: null }],
    }));
  };

  const navigateToWith = (screenKey, activeTab, additionalContext) => {
    setContext((prevState) => ({
      ...prevState,
      currentScreen: screenKey,
      activeTab: activeTab,
      additionalContext: additionalContext,
      viewStack: [...prevState.viewStack, { screen: screenKey, externalReference: additionalContext }],
    }));
  };

  const navigateBack = useCallback(() => {
    context.viewStack.pop();
    const screenToSet = context.viewStack[context.viewStack.length - 1];

    setContext((prevState) => ({
      ...prevState,
      currentScreen: screenToSet.screen,
      additionalContext: screenToSet.externalReference,
      viewStack: [...context.viewStack],
    }));
  }, [setContext, context.viewStack]);

  const showToaster = (data) => {
    const title = data.responseStatus;
    const message = data.responseMessage;
    const color = title === 'Success' ? '#04b14b' : '#C8242C';
    const borderColor = title === 'Success' ? '#017e34' : '#8f1920';

    setToasterData({ title, message, color, borderColor });
    setTimeout(() => {
      setToasterData(null);
    }, 8000);
  };

  useEffect(() => {
    window.addEventListener(
      'resize',
      () => {
        const isMobile = window.innerWidth < 1000;
        if (isMobile) {
          setShowMenu(false);
        } else {
          setShowMenu(true);
        }
      },
      false
    );
  });

  useEffect(() => {
    let screen = screenMap.get(context.currentScreen);
    if (screen === undefined || screen === null) {
      screen = <Construction navigateBack={navigateBack} />;
    }
    setActiveScreen(screen);
  }, [context, screenMap, navigateBack]);

  // DASHBOARD SCREENS
  screenMap.set(
    'dashboard',
    <Dashboard
      context={context}
      setContext={setContext}
      showToaster={showToaster}
      setSpinnerText={setSpinnerText}
      navigateTo={navigateTo}
    />
  );

  // CLIENTS SCREENS
  screenMap.set(
    'clientsSearch',
    <ClientsSearch
      context={context}
      setContext={setContext}
      navigateTo={navigateTo}
      navigateToWith={navigateToWith}
      navigateBack={navigateBack}
      showToaster={showToaster}
      setSpinnerText={setSpinnerText}
    />
  );

  screenMap.set(
    'clientsCreate',
    <ClientsCreate
      context={context}
      setContext={setContext}
      navigateTo={navigateTo}
      navigateToWith={navigateToWith}
      navigateBack={navigateBack}
      setSpinnerText={setSpinnerText}
      showToaster={showToaster}
      toolTipsEnabled={toolTipsEnabled}
    />
  );

  screenMap.set(
    'clientsView',
    <ClientsView
      context={context}
      setContext={setContext}
      navigateTo={navigateTo}
      navigateToWith={navigateToWith}
      navigateBack={navigateBack}
      setSpinnerText={setSpinnerText}
      showToaster={showToaster}
      toolTipsEnabled={toolTipsEnabled}
    />
  );

  // STUDY & TRIAL SCREENS
  screenMap.set(
    'studyTrialAdmin',
    <StudyTrialSearch
      context={context}
      setContext={setContext}
      navigateTo={navigateTo}
      navigateBack={navigateBack}
      showToaster={showToaster}
      setSpinnerText={setSpinnerText}
      navigateToWith={navigateToWith}
    />
  );

  screenMap.set(
    'formAdmin',
    <FormAdministrationSearch
      navigateTo={navigateTo}
      context={context}
      setContext={setContext}
      setSpinnerText={setSpinnerText}
      showToaster={showToaster}
      navigateToWith={navigateToWith}
    />
  );

  screenMap.set(
    'formAdminCreate',
    <CreateNewForm
      navigateTo={navigateTo}
      context={context}
      setContext={setContext}
      setSpinnerText={setSpinnerText}
      showToaster={showToaster}
      toolTipsEnabled={toolTipsEnabled}
    />
  );

  screenMap.set(
    'participants',
    <ParticipantsSearch
      context={context}
      setContext={setContext}
      navigateToWith={navigateToWith}
      showToaster={showToaster}
      setSpinnerText={setSpinnerText}
    />
  );

  screenMap.set(
    'participantsLinkNew',
    <ParticipantsLinkNew
      context={context}
      navigateTo={navigateTo}
      navigateToWith={navigateToWith}
      navigateBack={navigateBack}
      setSpinnerText={setSpinnerText}
      showToaster={showToaster}
      toolTipsEnabled={toolTipsEnabled}
    />
  );

  screenMap.set(
    'participantEdit',
    <ParticipantsEditTab
      context={context}
      setContext={setContext}
      navigateTo={navigateTo}
      navigateBack={navigateBack}
      setSpinnerText={setSpinnerText}
      showToaster={showToaster}
      toolTipsEnabled={toolTipsEnabled}
      navigateToWith={navigateToWith}
    />
  );

  screenMap.set(
    'studyTrialCreate',
    <StudyTrialCreate
      context={context}
      setContext={setContext}
      navigateTo={navigateTo}
      navigateBack={navigateBack}
      setSpinnerText={setSpinnerText}
      showToaster={showToaster}
      toolTipsEnabled={toolTipsEnabled}
    />
  );

  screenMap.set(
    'studyTrialEdit',
    <StudyTrialEdit
      context={context}
      setContext={setContext}
      navigateTo={navigateTo}
      navigateToWith={navigateToWith}
      navigateBack={navigateBack}
      setSpinnerText={setSpinnerText}
      showToaster={showToaster}
      toolTipsEnabled={toolTipsEnabled}
    />
  );

  // REVIEWS AND QUERIES SCREENS
  screenMap.set(
    'participantReviewsTable',
    <ParticipantReviewSearch
      navigateTo={navigateTo}
      navigateBack={navigateBack}
      context={context}
      setContext={setContext}
      setSpinnerText={setSpinnerText}
      showToaster={showToaster}
      toolTipsEnabled={toolTipsEnabled}
      navigateToWith={navigateToWith}
    />
  );

  screenMap.set(
    'participantReviewsCreate',
    <ParticipantReviewCreate
      context={context}
      setContext={setContext}
      navigateTo={navigateTo}
      navigateToWith={navigateToWith}
      navigateBack={navigateBack}
      setSpinnerText={setSpinnerText}
      showToaster={showToaster}
      toolTipsEnabled={toolTipsEnabled}
    />
  );

  screenMap.set(
    'participantReviewsEdit',
    <ParticipantReviewEdit
      context={context}
      setContext={setContext}
      navigateTo={navigateTo}
      navigateBack={navigateBack}
      setSpinnerText={setSpinnerText}
      showToaster={showToaster}
      toolTipsEnabled={toolTipsEnabled}
    />
  );

  screenMap.set(
    'capaReviewsTable',
    <CAPAReviewSearch
      navigateTo={navigateTo}
      navigateBack={navigateBack}
      context={context}
      setContext={setContext}
      setSpinnerText={setSpinnerText}
      showToaster={showToaster}
      toolTipsEnabled={toolTipsEnabled}
      navigateToWith={navigateToWith}
    />
  );

  screenMap.set(
    'capaReviewsCreate',
    <CAPAReviewCreate
      context={context}
      navigateTo={navigateTo}
      navigateBack={navigateBack}
      setSpinnerText={setSpinnerText}
      showToaster={showToaster}
      toolTipsEnabled={toolTipsEnabled}
    />
  );

  screenMap.set(
    'capaReviewsEdit',
    <CAPAReviewEdit
      context={context}
      navigateTo={navigateTo}
      setSpinnerText={setSpinnerText}
      showToaster={showToaster}
      toolTipsEnabled={toolTipsEnabled}
    />
  );
  // ADMIN SCREENS
  screenMap.set(
    'usersTable',
    <UsersSearch
      context={context}
      navigateTo={navigateTo}
      navigateBack={navigateBack}
      setSpinnerText={setSpinnerText}
      showToaster={showToaster}
      toolTipsEnabled={toolTipsEnabled}
      navigateToWith={navigateToWith}
    />
  );

  screenMap.set(
    'usersView',
    <UsersView
      context={context}
      setContext={setContext}
      navigateTo={navigateTo}
      navigateBack={navigateBack}
      setSpinnerText={setSpinnerText}
      showToaster={showToaster}
      toolTipsEnabled={toolTipsEnabled}
    />
  );

  screenMap.set(
    'facilitiesManagement',
    <FacilitiesSearch context={context} showToaster={showToaster} setSpinnerText={setSpinnerText} />
  );

  screenMap.set(
    'clientSecurityQuestions',
    <ClientSecurityQuestions
      navigateTo={navigateTo}
      context={context}
      setContext={setContext}
      setSpinnerText={setSpinnerText}
      showToaster={showToaster}
      toolTipsEnabled={toolTipsEnabled}
    />
  );

  screenMap.set(
    'genericQueryComments',
    <GenericQueryComments
      navigateTo={navigateTo}
      context={context}
      setContext={setContext}
      setSpinnerText={setSpinnerText}
      showToaster={showToaster}
      toolTipsEnabled={toolTipsEnabled}
    />
  );

  screenMap.set(
    'capaNotificationSettings',
    <CapaNotificationSettings
      navigateTo={navigateTo}
      context={context}
      setContext={setContext}
      setSpinnerText={setSpinnerText}
      showToaster={showToaster}
      toolTipsEnabled={toolTipsEnabled}
    />
  );
  // REPORT SCREENS
  screenMap.set(
    'dataManagement',
    <DataManagement context={context} setSpinnerText={setSpinnerText} showToaster={showToaster} />
  );
  screenMap.set(
    'qualityControl',
    <QualityControl context={context} setSpinnerText={setSpinnerText} showToaster={showToaster} />
  );

  // SETTINGS SCREEN
  screenMap.set(
    'settings',
    <Settings context={context} setContext={setContext} showToaster={showToaster} setSpinnerText={setSpinnerText} />
  );

  screenMap.set('noAccess', <NoAccess keycloak={keycloak} />);

  return (
    <div className="view-container">
      <div className="view-header">
        <ClineaseLogo />
        <AurumLogo />
      </div>
      <div className="view-content">
        {context.currentScreen !== 'noAccess' && (
          <SideMenu
            showMenu={showMenu}
            context={context}
            navigateTo={navigateTo}
            setContext={setContext}
            keycloak={keycloak}
            setShowMenu={setShowMenu}
          />
        )}
        <div className={'view-active-screen-content ' + (!showMenu ? 'center' : null)}>
          {activeScreen}
          {toasterData && (
            <Toaster
              title={toasterData.title}
              message={toasterData.message}
              color={toasterData.color}
              borderColor={toasterData.borderColor}
            />
          )}
        </div>
      </div>
    </div>
  );
};
